import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  Metrics,
  TrendInfo,
  TrendGranularity,
} from 'src/app/modules/campaign-insight/campaign-insight.interface';
import { CommonGridComponent } from 'src/app/shared/components/common-grid/common-grid.component';
import { PaginationStateService } from 'src/app/shared/services/pagination-state.service';
import { ApiConfig } from 'src/app/shared/components/common-grid/common-grid.interface';

export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!Array.isArray(control?.value) && control?.value) {
      const isWhitespace = control?.value?.trim()?.length === 0;
      const isValid = !isWhitespace;
      return isValid ? null : { whitespace: true };
    } else {
      return null;
    }
  };
}

export function numericValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control?.value === null || control?.value === '') {
      return null;
    }

    const isValid = /^\d+$/.test(control?.value);

    return isValid ? null : { numeric: true };
  };
}

// ===== Budget calculation helpers (shared) =====
export interface BudgetInfo {
  totalBudget?: number;
  reservedAmount?: number;
  refundedAmount?: number;
  maxBudgetPercentage?: number;
}

export const getEffectiveTotalBudget = (info: BudgetInfo): number => {
  const total = info?.totalBudget ?? 0;
  const percent = info?.maxBudgetPercentage ?? null;
  if (percent && percent > 0 && percent <= 100) {
    return (total * percent) / 100;
  }
  return total;
};

export const getAvailableBudget = (info: BudgetInfo): number => {
  const effectiveTotal = getEffectiveTotalBudget(info);
  const reserved = info?.reservedAmount ?? 0;
  const refunded = info?.refundedAmount ?? 0;
  return effectiveTotal + refunded - reserved;
};

export const getBudgetUsagePercentage = (info: BudgetInfo): number => {
  const effectiveTotal = getEffectiveTotalBudget(info);
  if (!effectiveTotal) return 0;
  const reserved = info?.reservedAmount ?? 0;
  const refunded = info?.refundedAmount ?? 0;
  const netReserved = reserved - refunded;
  return (netReserved / effectiveTotal) * 100;
};

export const getBudgetThresholdClass = (percentage: number): string => {
  if (percentage <= 0) return ' draft-status ';
  if (percentage < 90) return ' draft-status ';
  if (percentage < 100) return ' orange-text ';
  return ' failed-text ';
};

// ===== Campaign Insight Trend Calculation Helpers =====
export const calculateHasTrends = (metricsData: Metrics): boolean => {
  if (Object.keys(metricsData?.previousPeriod || {}).length === 0) return false;
  const prev = metricsData.previousPeriod;
  return prev?.summary !== null;
};

export const calculateTrend = (
  currentValue: number,
  previousValue: number,
  hasTrends?: boolean,
): TrendInfo | null => {
  if (hasTrends !== undefined && !hasTrends) return null;

  let change: number;

  if (previousValue === 0) {
    // When previous is 0, calculate as (current - previous) * 100
    change = (currentValue - previousValue) * 100;
  } else {
    // Normal percentage calculation with division
    change = ((currentValue - previousValue) / previousValue) * 100;
  }

  const direction: 'up' | 'down' | 'neutral' =
    change > 0.1 ? 'up' : change < -0.1 ? 'down' : 'neutral';

  return {
    direction,
    value: `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`,
    arrow: direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→',
  };
};

export const getTrendLabel = (trendGranularity: TrendGranularity): string => {
  switch (trendGranularity) {
    case 'day':
      return 'from <strong>Yesterday</strong>';
    case 'week':
      return 'from <strong>Past Week</strong>';
    case 'month':
      return 'from <strong>Past Month</strong>';
    case 'year':
      return 'from <strong>Past Year</strong>';
    default:
      return 'from <strong>Previous Period</strong>';
  }
};

export const getTrendDirectionText = (
  direction: 'up' | 'down' | 'neutral' | undefined,
  trendGranularity: TrendGranularity,
): string => {
  if (!direction) return '';

  const directionText =
    direction === 'up' ? 'Up' : direction === 'down' ? 'Down' : 'Same';
  const trendLabel = getTrendLabel(trendGranularity);
  return `${directionText} ${trendLabel}`;
};

export const formatTrendText = (
  trendInfo: TrendInfo | null,
  trendLabel: string,
): string => {
  if (!trendInfo) return '';

  const directionText =
    trendInfo.direction === 'up'
      ? 'Up'
      : trendInfo.direction === 'down'
        ? 'Down'
        : 'Same';

  return `${trendInfo.value} ${directionText} ${trendLabel}`;
};

// ===== Pagination State Management Utilities =====
export interface PaginationStateOptions {
  route: string;
  gridName: string;
  paginationService: PaginationStateService;
  apiConfig: ApiConfig;
  componentName: string;
  formType?: string;
  selectedChannel?: string;
  gridComponent?: CommonGridComponent;
}

/**
 * Clear current grid state (used when switching tabs)
 */
export const clearGridState = (options: PaginationStateOptions): void => {
  const stateKey = `${options.route}_${options.gridName}`;
  options.paginationService.clearState(stateKey);
};

/**
 * Save current state before navigating to detail view
 */
export const saveStateBeforeNavigation = (
  options: PaginationStateOptions,
): void => {
  const stateKey = `${options.route}_${options.gridName}`;

  // Calculate current page number (convert from 0-based to 1-based)
  const currentPageNumber =
    Math.floor(options.apiConfig.requestBody.pageNumber || 0) + 1;
  const currentPageSize = options.apiConfig.requestBody.pageSize || 10;

  // Get current filter and sorting state from grid component
  const currentFilters = options.gridComponent?.currentFilters;
  const currentSortField = options.gridComponent?.sortingField;
  const currentSortDirection = options.gridComponent?.sortingDirection;

  options.paginationService.saveExtendedState(
    stateKey,
    currentPageNumber,
    currentPageSize,
    options.selectedChannel,
    options.formType,
    {
      filters: currentFilters,
      sortField: currentSortField,
      sortDirection: currentSortDirection,
    },
  );
};

/**
 * Apply restored page size to API config
 */
export const applyRestoredPageSize = (
  options: PaginationStateOptions,
): void => {
  const stateKey = `${options.route}_${options.gridName}`;
  const savedState = options.paginationService.getExtendedState(stateKey);

  // Apply saved page size to API config if it exists and we have an existing API config
  if (
    savedState.pageSize &&
    savedState.pageSize !== 10 &&
    options.apiConfig?.requestBody
  ) {
    options.apiConfig.requestBody.pageSize = savedState.pageSize;
  }
};

/**
 * Apply restored page size to API config with context matching (for templates)
 */
export const applyRestoredPageSizeWithContext = (
  options: PaginationStateOptions,
): void => {
  const stateKey = `${options.route}_${options.gridName}`;
  const savedState = options.paginationService.getExtendedState(stateKey);

  // Apply saved pagination state if it exists and matches current context
  if (savedState.pageNumber || savedState.pageSize) {
    // Only restore state if the saved context matches current context (case-insensitive)
    const channelMatches =
      savedState.selectedChannel &&
      options.selectedChannel &&
      savedState.selectedChannel.toLowerCase() ===
        options.selectedChannel.toLowerCase();
    const tabTypeMatches = savedState.tabType === options.formType;
    const contextMatches = channelMatches && tabTypeMatches;

    if (contextMatches) {
      // Apply saved page number (convert from 1-based to 0-based)
      if (savedState.pageNumber && savedState.pageNumber > 1) {
        options.apiConfig.requestBody.pageNumber = savedState.pageNumber - 1;
      }

      // Apply saved page size
      if (savedState.pageSize && savedState.pageSize !== 10) {
        options.apiConfig.requestBody.pageSize = savedState.pageSize;
      }
    }
  }
};

/**
 * Force reset PrimeNG table to page 1 (for tab switching)
 */
export const forceResetTablePagination = (
  gridComponent?: CommonGridComponent,
  componentName?: string,
): void => {
  if (gridComponent && gridComponent.commonTable) {
    gridComponent.commonTable.first = 0;
    gridComponent.commonTable.rows = 10;
    gridComponent.commonTable.reset();
  }
};

/**
 * Check and apply restored state during component initialization
 */
export const checkForRestoredState = (options: PaginationStateOptions): any => {
  const stateKey = `${options.route}_${options.gridName}`;
  const savedState = options.paginationService.getExtendedState(stateKey);

  return savedState;
};
